import { useCallback, useState } from 'react';
import pluralize from 'pluralize';
import { ButtonBase, Collapse, Stack, Typography, styled } from '@mui/material';

import { type ConnectStatementsInstitution } from '../hooks/useConnectStatementsData';

import { accountTypeLabel } from './InstitutionStatementCard';

import CheckSmallIcon from 'modules/common/components/Icons/CheckSmallIcon';
import Tag from 'modules/common/components/MUI/Tag';
import RotableChevron from 'modules/common/components/Icons/RotableChevron';
import BankLogo from 'modules/HubV2/Bookkeeping/components/Banks2/BankList/Bank/BankLogo';

export type SyncInstitutionStatus = 'enabled' | 'confirming' | 'not_set_up' | 'ready_to_sync' | 'unchecked';

const STATUS_LABELS: Record<Exclude<SyncInstitutionStatus, 'enabled'>, string> = {
    confirming: 'Confirming…',
    not_set_up: 'Not set up',
    ready_to_sync: 'Ready to sync',
    unchecked: 'Couldn’t check',
};

const Row = styled('div')(({ theme }) => ({
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    '&:last-child': {
        borderBottom: 0,
    },
}));

const AccountsToggle = styled(ButtonBase)(({ theme }) => ({
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
    gap: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
}));

const ToggleChevron = styled(RotableChevron)(({ theme }) => ({
    fontSize: theme.spacing(2),
}));

interface SyncInstitutionRowProps {
    readonly institution: ConnectStatementsInstitution;
    readonly status: SyncInstitutionStatus;
    /** Whether the account count opens the list of accounts, or is just a count. */
    // eslint-disable-next-line react/require-default-props
    readonly expandable?: boolean;
}

/** One connected bank: its logo and name, its accounts, and its status. */
export default function SyncInstitutionRow({
    institution,
    status,
    expandable = true,
}: Readonly<SyncInstitutionRowProps>) {
    const [isExpanded, setIsExpanded] = useState(false);
    const accountCount = institution.accounts.length;
    const accountsId = `sync-accounts-${institution.institutionId}`;

    const toggleExpanded = useCallback(() => setIsExpanded((prev) => !prev), []);

    return (
        <Row>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
                    <BankLogo institutionId={institution.institutionId} width={32} height={32} />
                    {/* A column, not inline flow: bodySemiBold has no variantMapping, so it renders as a
                        <span> and the toggle would otherwise sit on the name's line. */}
                    <Stack alignItems="flex-start" sx={{ minWidth: 0 }}>
                        <Typography variant="bodySemiBold" noWrap>
                            {institution.institutionName}
                        </Typography>
                        {expandable ? (
                            <AccountsToggle
                                onClick={toggleExpanded}
                                aria-expanded={isExpanded}
                                aria-controls={accountsId}>
                                {pluralize('account', accountCount, true)}
                                <ToggleChevron from="down" to="up" isRotated={isExpanded} />
                            </AccountsToggle>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {pluralize('account', accountCount, true)}
                            </Typography>
                        )}
                    </Stack>
                </Stack>
                {status === 'enabled' ? (
                    <Tag color="success" variant="filled" label="Synced" icon={<CheckSmallIcon fontSize="inherit" />} />
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {STATUS_LABELS[status]}
                    </Typography>
                )}
            </Stack>
            <Collapse in={expandable && isExpanded} id={accountsId}>
                <Stack spacing={1} sx={{ pt: 1.5, pl: 6 }}>
                    {institution.accounts.map((account) => (
                        <Typography key={account.id} variant="body2" color="text.secondary" noWrap>
                            {account.name} · {accountTypeLabel(account.type)} • {account.mask}
                        </Typography>
                    ))}
                </Stack>
            </Collapse>
        </Row>
    );
}
